import { GripHorizontal, GripVertical, Add } from '@/components/icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { DragEvent } from 'react';

interface TableEdgePillProps {
    orientation: 'row' | 'column';
    /** Pixel offset in the overlay layer: top (row) / left (column). */
    offset: number;
    /** Keep mounted but invisible while its drag is in flight — unmounting
     *  the drag source mid-drag cancels the HTML5 drag. */
    dragging: boolean;
    /** Hide the grip (render only the + button) when there is nothing to reorder. */
    showGrip: boolean;
    onDragStart: (event: DragEvent) => void;
    onDragEnd: () => void;
    onSelect: () => void;
    onContextSelect: () => void;
    /** Insert a row below / column to the right of the hovered one. */
    onAdd: () => void;
    addTitle: string;
}

/**
 * Floating controls shown next to the hovered row (left edge) or above the
 * hovered column (top edge): a draggable grip plus a "+" insert button,
 * Google-Docs style. The container touches the table edge so moving the
 * pointer from a cell onto the pill never leaves the table wrapper.
 */
export function TableEdgePill({
    orientation,
    offset,
    dragging,
    showGrip,
    onDragStart,
    onDragEnd,
    onSelect,
    onContextSelect,
    onAdd,
    addTitle,
}: TableEdgePillProps) {
    const isRow = orientation === 'row';

    return (
        <div
            className={cn(
                'absolute z-20 flex items-center justify-center gap-0.5',
                isRow ? '-left-6 w-6 flex-col' : '-top-6 h-6 flex-row',
                dragging && 'opacity-0',
            )}
            style={
                isRow
                    ? { top: offset, transform: 'translateY(-50%)' }
                    : { left: offset, transform: 'translateX(-50%)' }
            }
        >
            <TooltipProvider delayDuration={400}>
                {showGrip && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                draggable
                                onDragStart={onDragStart}
                                onDragEnd={onDragEnd}
                                onMouseDown={(event) => event.stopPropagation()}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onSelect();
                                }}
                                onContextMenu={onContextSelect}
                                className={cn(
                                    'flex cursor-grab items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 shadow-sm hover:text-slate-600 active:cursor-grabbing',
                                    isRow ? 'h-6 w-4' : 'h-4 w-7',
                                )}
                                aria-label="Arraste para reorganizar"
                            >
                                {isRow ? <GripVertical size={12} /> : <GripHorizontal size={12} />}
                            </button>
                        </TooltipTrigger>
                        {/* Away from the table, mirroring where the pill sits. */}
                        <TooltipContent side={isRow ? 'left' : 'top'}>
                            Arraste para reorganizar
                        </TooltipContent>
                    </Tooltip>
                )}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            type="button"
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                                event.stopPropagation();
                                onAdd();
                            }}
                            className="flex size-4 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 shadow-sm hover:text-slate-600"
                            aria-label={addTitle}
                        >
                            <Add size={10} />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side={isRow ? 'left' : 'top'}>{addTitle}</TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    );
}
